import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, ExternalLink, X } from "lucide-react";
import { Evidence } from "./bits";

// Section 5: award / loss / hold / cancel and paperwork suggestions wait here for one click. People record outcomes.
export function SuggestionList({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("pending");
  const load = (st = status) => api.get("/bd/suggestions", { params: { status: st } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(status); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (s, kind) => {
    const reason = kind === "dismiss" ? (window.prompt("Why dismiss? (optional)") ?? null) : "";
    if (reason === null) return;
    try { await api.post(`/bd/suggestions/${s.suggestion_id}/${kind}`, kind === "dismiss" ? { reason } : {}); toast.success(kind === "confirm" ? "Applied" : "Dismissed"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };
  const tabs = (
    <div className="flex gap-1 mb-2" data-testid="bd-suggestion-tabs">
      {[["pending", "Waiting"], ["confirmed", "Confirmed"], ["dismissed", "Dismissed"]].map(([k, l]) => (
        <button key={k} onClick={() => { setRows(null); setStatus(k); }} data-testid={`bd-suggestion-tab-${k}`}
          className={`text-xs px-3 py-1 rounded-full border ${status === k ? "bg-[#003E87] text-white border-[#003E87]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
      ))}
    </div>
  );
  if (rows === null) return <div>{tabs}<div className="text-sm text-slate-400 py-12 text-center">Loading…</div></div>;
  if (!rows.length) return <div>{tabs}<Card className="p-10 text-center text-sm text-slate-500" data-testid="bd-suggestions-empty">{status === "pending" ? "No suggestions waiting. Awards, losses, holds and returned paperwork land here for your click." : `Nothing ${status} yet.`}</Card></div>;
  const what = (s) => s.kind === "stage" ? `Move “${s.bid_name || s.bid_id}” → ${s.to_status.replace("_", " ")}` : `${s.client_name || s.client_id}: ${s.field.toUpperCase()} → ${s.proposed?.status || "note"}${s.proposed?.paper ? ` (${s.proposed.paper} paper)` : ""}`;
  return (
    <div className="space-y-2" data-testid="bd-suggestion-list">
      {tabs}
      {rows.map((s) => (
        <Card key={s.suggestion_id} className="p-3 flex items-start gap-3" data-testid={`bd-suggestion-${s.suggestion_id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-black">{what(s)}</div>
            <div className="text-xs text-slate-600 mt-0.5">{s.summary} <span className="text-slate-400">· {fmtDate(s.created_at)}</span>{s.attachments?.length ? <span className="text-slate-400"> · {s.attachments.map((a) => a.filename).join(", ")}</span> : null}</div>
            <Evidence items={s.evidence} />
            {s.status === "confirmed" && (
              <div className="text-xs text-emerald-700 mt-1" data-testid={`bd-suggestion-confirmed-${s.suggestion_id}`}>
                Confirmed by <b>{s.confirmed_by}</b>{s.confirmed_via === "claude" ? " via Claude" : ""} · {fmtDate(s.confirmed_at)}{s.basis ? <> · “{s.basis}”</> : null}
              </div>
            )}
            {s.status === "dismissed" && <div className="text-xs text-slate-500 mt-1">Dismissed by {s.dismissed_by} · {fmtDate(s.dismissed_at)}{s.reason ? ` · ${s.reason}` : ""}</div>}
            {(s.history || []).map((h, i) => <div key={i} className="text-[11px] text-slate-400">Was confirmed by {h.confirmed_by}{h.via === "claude" ? " via Claude" : ""}{h.basis ? ` (“${h.basis}”)` : ""}, undone by {h.undone_by} · {fmtDate(h.undone_at)}</div>)}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {s.gmail_link && <a href={s.gmail_link} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#003E87]"><ExternalLink className="w-3.5 h-3.5" /></a>}
            {s.status === "pending" && <>
            <Button size="sm" onClick={() => act(s, "confirm")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`bd-suggestion-confirm-${s.suggestion_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Confirm</Button>
            <Button size="sm" variant="outline" onClick={() => act(s, "dismiss")} data-testid={`bd-suggestion-dismiss-${s.suggestion_id}`}><X className="w-3.5 h-3.5 mr-1" /> Dismiss</Button>
            </>}
          </div>
        </Card>
      ))}
    </div>
  );
}
